// De maatschappelijke besparing van materialen en coachadviezen over één
// organisatie, begrensd op één meetperiode. Dit is de derde waardestroom in de
// teller van Impact per euro.
//
// ALLEEN DE MAATSCHAPPELIJKE BESPARING. Een registratie levert drie uitkomsten:
// besparing op de energierekening, bespaarde CO2, en de maatschappelijke
// besparing. Alleen de laatste is maatschappelijke waarde; de eerste twee zijn
// MSI-outputs met een eigen eenheid en horen in de benchmark. Dit bestand geeft
// ze daarom niet terug — niet als extra veld, niet "voor het gemak". Optellen
// zou dezelfde fysieke besparing twee keer tellen en het geld van de bewoner
// als maatschappelijke opbrengst labelen.
// AI-CONTEXT: See docs/methodiek-impact-per-euro.md § 1.3
//
// PER HUISHOUDEN EERST, DAN PAS OPTELLEN. Een per-bewoner- of per-woning-code
// is een heel-huishouden-getal; over huishoudens heen doorrekenen alsof het één
// woning is zou het bewonersaantal van de hele tenant opleveren. Elk huishouden
// gaat daarom door computeHouseholdImpact heen en pas het resultaat wordt
// opgeteld.
//
// BUITEN DE PERIODE IS GEEN GAT. Een registratie van vorig boekjaar hoort hier
// niet en is niets om te melden. Een registratie ZONDER datum is dat wel: die
// kan aan geen enkele periode worden toegewezen, en stil weglaten of stil
// meetellen zijn allebei fout.
//
// DE PERIODE WORDT HIER GEFILTERD, NIET IN DE QUERY. Een Firestore-range op
// `RegisteredAt` sluit elk document uit dat het veld niet heeft, en dat zijn
// precies alle registraties van vóór T1 — die zouden dan stil uit het totaal
// verdwijnen in plaats van op hun benaderde datum mee te tellen. De aanroeper
// haalt dus op `CompagnyID` op (één equality, geen composite index nodig) en
// laat het begrenzen aan deze helper over.
// AI-CONTEXT: docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
//
// WERPT NOOIT, en een onbekende is nooit een nul.
// AI-CONTEXT: docs/pitfalls.md#ui-strict-validator-kills-autosave
//
// AI-CONTEXT: See docs/plans/ondersteuningsmodule-code-integratie.md (T2)

import notDeleted from '../notDeleted'
import computeHouseholdMaterials from './computeHouseholdMaterials'
import computeHouseholdImpact from './computeHouseholdImpact'
import { readRegistrationDate } from './registrationDate'

/**
 * @typedef {object} PeriodUnknown
 * @property {string} adressID - Huishouden waar de registratie op staat
 * @property {string} materialID
 * @property {string} title
 * @property {string} status - Waarom er geen bedrag uit kwam
 *
 * @typedef {object} MaterialPeriodValue
 * @property {number|null} societalEur - Maatschappelijke besparing over de periode; null als er niets berekenbaar was
 * @property {number} householdCount - Huishoudens die een bedrag opleverden
 * @property {number} knownCount - Registraties die meetelden
 * @property {number} unknownCount
 * @property {Array<PeriodUnknown>} unknown - Wat er buiten het totaal viel, met reden
 * @property {number} approximateCount - Meegetelde registraties die op hun laatste wijziging terugvielen
 */

/** @type {MaterialPeriodValue} */
const EMPTY = {
    societalEur: null,
    householdCount: 0,
    knownCount: 0,
    unknownCount: 0,
    unknown: [],
    approximateCount: 0,
}

/**
 * @param {object} input
 * @param {Array<object>} input.entries - Alle `AdressMaterials` van de tenant (mag ongefilterd)
 * @param {Array<object>} input.materials - De `Materials`-hoofdlijst
 * @param {{byCode: object}|null} input.library - Uit adaptInterventieDocument; null bij laden of fout
 * @param {Object<string, number|null>} [input.residentsByAdress] - Bewonersaantal per adres
 * @param {number} input.startMs - Periodestart (epoch-ms, inclusief)
 * @param {number} input.endMs - Periode-einde (epoch-ms, inclusief)
 * @returns {MaterialPeriodValue}
 */
const sumMaterialPeriodValue = (input) => {
    const entries = Array.isArray(input?.entries) ? input.entries : []
    const materials = Array.isArray(input?.materials) ? input.materials : []
    const library = input?.library
    const residentsByAdress = input?.residentsByAdress || {}
    const { startMs, endMs } = input || {}

    // Geen bibliotheek betekent geen enkel bedrag — nooit een nul. Zonder
    // standaard is een niet-gekoppeld materiaal niet te onderscheiden van een
    // materiaal dat werkelijk niets bespaart.
    if (!library?.byCode || !entries.length) return EMPTY

    const inPeriode = Number.isFinite(startMs) && Number.isFinite(endMs)

    /** @type {Map<string, Array<object>>} */
    const perAdress = new Map()
    const unknown = []
    let approximateCount = 0

    entries.filter(notDeleted).forEach((entry) => {
        const adressID = entry?.AdressID || ''
        const { ms, source } = readRegistrationDate(entry)

        if (source === 'none') {
            unknown.push({
                adressID,
                materialID: entry?.MaterialID || '',
                title: '',
                status: 'needs-date',
            })
            return
        }

        if (inPeriode && (ms < startMs || ms > endMs)) return

        if (source === 'legacy') approximateCount += 1

        const bucket = perAdress.get(adressID)
        if (bucket) bucket.push(entry)
        else perAdress.set(adressID, [entry])
    })

    let societalEur = null
    let householdCount = 0
    let knownCount = 0

    perAdress.forEach((adressEntries, adressID) => {
        const { rows } = computeHouseholdMaterials({ entries: adressEntries, materials })
        const impact = computeHouseholdImpact({
            rows,
            materials,
            library,
            residents: residentsByAdress[adressID],
        })

        impact.unknown.forEach((u) => unknown.push({ ...u, adressID }))
        knownCount += impact.knownCount

        if (impact.societalEur === null) return

        societalEur = (societalEur ?? 0) + impact.societalEur
        householdCount += 1
    })

    return {
        societalEur,
        householdCount,
        knownCount,
        unknownCount: unknown.length,
        unknown,
        approximateCount,
    }
}

export default sumMaterialPeriodValue
